import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isDeletedStaff from '@medflex/utils/isDeletedStaff';
import config from '@medflex/utils/config';
import { USER_TYPES, CHAT_MESSAGE_TYPE } from '@medflex/utils/constants';
import isSupportPatient from '@medflex/utils/isSupportPatient';
import ContactMessages from '../store/models/contactMessages';
import Contacts from '../store/models/chatContacts';
import FullNameWithTitle from '../../utils/getFullNameWithTitle';

const useMessages = () => {
  const contactMessages = useSelector(({ [ContactMessages.name]: m }) => m.messages || []);
  const contact = useSelector(({ [ContactMessages.name]: m }) => m.contact);
  const autoResponseMessage = useSelector(({ [ContactMessages.name]: m }) => m.autoResponseMessage);
  const contacts = useSelector(({ [Contacts.name]: c }) => c);
  const superuser = useSelector(({ superuser: user }) => user);
  const isSending = useSelector(state => state.loading.effects[ContactMessages.name].send);
  const isLoading = useSelector(state => state.loading.effects[ContactMessages.name].find);
  const dispatch = useDispatch();
  const { errors } = useSelector(state => ({
    errors: state.errors.chat_messages,
  }));

  const markImportant = props => {
    dispatch[ContactMessages.name].markImportant(props);
  };

  const markChecked = props => {
    dispatch[ContactMessages.name].markChecked(props);
  };

  const deleteMessage = ({ id }) => dispatch[ContactMessages.name].delete({ id });

  const send = payload => {
    if (superuser.type === USER_TYPES.PATIENT) {
      const { recipient, text, files } = payload;
      const myContact = contacts.find(c => c.user.id === recipient);
      if (myContact.mainContactId) {
        const mainContact = contacts.find(c => c.id === myContact.mainContactId);
        return dispatch[ContactMessages.name].send({
          recipient: mainContact.user.id,
          sender: recipient,
          text,
          files,
        });
      }
    }
    return dispatch[ContactMessages.name].send(payload);
  };

  const sendMulticast = payload => dispatch[ContactMessages.name].sendMulticast(payload);

  const find = useCallback(
    obj => {
      dispatch[ContactMessages.name].find(obj);
    },
    [dispatch],
  );

  const messages = useMemo(() => {
    if (contactMessages && contact) {
      return [
        ...(isSupportPatient(contact.user)
          ? [
              {
                id: 'supportWelcomeMessage',
                recipient: superuser.id,
                time: contact.createdAt,
                type: CHAT_MESSAGE_TYPE.SUPPORT_WELCOME_MESSAGE,
                isSender: false,
                user: {
                  id: config.MEDFLEX_SUPPORT_PATIENT_ID,
                  fullNameWithTitle: FullNameWithTitle.get({
                    id: config.MEDFLEX_SUPPORT_PATIENT_ID,
                  }),
                },
                sender: {},
              },
            ]
          : []),
        ...contactMessages
          .map(message => {
            const isSender =
              (contact.mainContactId &&
                ((superuser.type !== USER_TYPES.PATIENT &&
                  message.sender.id !== contact.user.id &&
                  superuser.id !== message.recipient) ||
                  (superuser.type === USER_TYPES.PATIENT &&
                    message.sender.id === contact.user.id))) ||
              message.sender.id === superuser.id;
            return {
              ...message,
              isSender,
              seen:
                message.time &&
                (isSender || message.sender.meOrSurrogate) &&
                contact.user.lastSeenMessageTimestamp &&
                new Date(message.time) <= new Date(contact.user.lastSeenMessageTimestamp),
              user: {
                ...message.sender,
                initials: isDeletedStaff(message.sender.firstName)
                  ? 'M'
                  : `${message.sender.firstName[0]}${message.sender.lastName[0]}`,
                fullNameWithTitle: FullNameWithTitle.get(message.sender),
              },
            };
          })
          .sort((a, b) => new Date(a.time) - new Date(b.time)),
      ];
    }
    return [];
  }, [contactMessages, contact]);

  return {
    messages,
    autoResponseMessage,
    send,
    sendMulticast,
    find,
    delete: deleteMessage,
    markImportant,
    markChecked,
    isSending,
    isLoading,
    errors,
  };
};

export default useMessages;
